import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import type { NavBarProps } from '../../../NavBar';
import { NavBar } from '../../../NavBar';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { GuestLanguageSelector } from '@apps/guest/components/GuestLanguageSelector';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventPageType } from '@graphql/generated-base';
import { Category, EventType } from '@graphql/generated-base';
import type { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type {
  HeaderTitleStyle,
  HorizontalAlignment,
  NavigationStyle,
  VerticalAlignment,
  WebsiteHeaderColors,
  WebsiteHeaderTextContent,
} from '@shared/types/WebsiteHeaderSettings.types';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { withWindow } from '@shared/utils/withWindow';
import { AnimatedBorder } from '@apps/guest/shared/components/InlineEditor/components/AnimatedBorder/AnimatedBorder';
import { useFitText } from '../../hooks/useFitText';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHeaderStylesForGuest } from '../../hooks/useHeaderStylesForGuest';
import { TextAlignmentToolbar } from './TextAlignmentToolbar';

export interface EventNameNavForStandaloneHeaderProps {
  navBarVariant: NavBarProps['variant'];
  eventHandle: string;
  pages: ReadonlyArray<EventPageFragment>;
  handleMenuClicked?: (item: string) => void;
  eventProps: NavbarEventPropsInterface;
  currentPageType: Maybe<EventPageType>;
  pageSlug?: string;
}

interface Props extends EventNameNavForStandaloneHeaderProps {
  colors?: WebsiteHeaderColors | undefined;
  headerTextData?: WebsiteHeaderTextContent | undefined;
  eventDisplayName: Maybe<string>;
  isCompactView: boolean;
  // Optional: layouts that hide the hero title (e.g. monogram + background) don't wire title editing.
  handleEditTextClick?: () => void;
  isContentEditable: boolean;
  editableContentRef?: React.RefObject<HTMLDivElement>;
  forceRenderViewIn?: 'mobile' | 'desktop';
  scaleValue?: number;
  renderedContainerHeight?: number;
  renderedContainerWidth?: number;
  /** Used in height/maxHeight calcs when renderedContainerHeight is not set (e.g. '90svh' when header is shortened for mobile). Defaults to '100svh'. */
  viewportHeightFallback?: string;
  headerTitleStyle?: HeaderTitleStyle;
  navigationStyle?: NavigationStyle;
  onTextAlignmentChange?: (alignment: HorizontalAlignment) => void;
  /** Suppresses the hero title (event display name) while keeping the nav bar title — used by monogram + background layouts where the monogram is the focal element. */
  hideEventName?: boolean;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

const computeResponsiveValue = <T,>(defaultValue: T, mobileValue: number, desktopValue: number, forceRenderViewIn?: 'mobile' | 'desktop') => {
  if (forceRenderViewIn === 'mobile') {
    return mobileValue;
  }
  if (forceRenderViewIn === 'desktop') {
    return desktopValue;
  }
  return defaultValue;
};

const DEFAULT_BORDER_RADIUS = 27;
const FULL_BORDER_RADIUS = 9999;
const FULL_BORDER_RADIUS_LINES_THRESHOLD = 1;
const TEXT_MAX_CHAR_LIMIT = 70;
const CONTAINER_TOTAL_MARGIN = 80;

const INPUT_AREA__PADDING_Y = 32;
const INPUT_AREA__PADDING_X_MOBILE = 24;
const INPUT_AREA__PADDING_X_DESKTOP = 40;
const INPUT_AREA__MARGIN_X_MOBILE = 24;
const INPUT_AREA__MARGIN_X_DESKTOP = 64;

const INLINE_WRAPPER_MIN_WIDTH = 180;
const INLINE_WRAPPER_MIN_HEIGHT = 100;

const getVerticalPlacement = (alignment: VerticalAlignment, forceRenderViewIn?: 'mobile' | 'desktop') => {
  switch (alignment) {
    case 'top':
      return { top: computeResponsiveValue({ _: 24, sm4: 0, lg: 0 }, 24, 0, forceRenderViewIn) };
    case 'center':
      return {
        top: '50%',
        transform: 'translateY(-50%)',
      };
    case 'bottom':
      return { bottom: computeResponsiveValue({ _: 24, md: 64, lg: 64 }, 24, 64, forceRenderViewIn) };
  }
};

export const EventNameNavForStandaloneHeader: FC<Props> = props => {
  const {
    handleMenuClicked,
    navBarVariant,
    eventHandle,
    eventProps,
    pages,
    eventDisplayName,
    colors,
    headerTextData,
    isCompactView,
    currentPageType,
    pageSlug,
    handleEditTextClick,
    isContentEditable = false,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    renderedContainerHeight,
    viewportHeightFallback = '100svh',
    headerTitleStyle,
    navigationStyle,
    onTextAlignmentChange,
    hideEventName = false,
  } = props;

  const navBarRef = useRef<HTMLDivElement>(null);
  // Read nav bar height into state so useFitText sees a stable value during render.
  // Reading navBarRef.current?.clientHeight directly in render is a stale-ref pattern:
  // it's 0 on the first render, then jumps when useFitText forces a re-render,
  // causing the containerRef Flex to resize and triggering a second calculateFontSize()
  // pass with 7+ forced synchronous DOM layout computations.
  const [navBarHeight, setNavBarHeight] = useState(0);
  useIsomorphicLayoutEffect(() => {
    if (navBarRef.current) {
      setNavBarHeight(navBarRef.current.clientHeight);
    }
  }, []);
  const textRef = useRef<HTMLDivElement>(null);
  // editableContentRef is not always available, so in that case we need to manage the ref ourselves
  const textRefToPass = editableContentRef || textRef;

  const [isMobile = false] = useResponsive({ values: { _: true, sm2: false } });
  const [isXsScreen = false] = useResponsive({ values: { _: true, xs: false } });

  const [fontSize = 64] = useResponsive({ values: { _: 64, md: 100, lg: 125 } });
  const [inputAreaMarginX = INPUT_AREA__MARGIN_X_MOBILE] = useResponsive({ values: { _: INPUT_AREA__MARGIN_X_MOBILE, md: INPUT_AREA__MARGIN_X_DESKTOP } });
  const [inputAreaPaddingX = INPUT_AREA__PADDING_X_MOBILE] = useResponsive({ values: { _: INPUT_AREA__PADDING_X_MOBILE, md: INPUT_AREA__PADDING_X_DESKTOP } });

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const responsiveInputAreaMarginX = computeResponsiveValue(inputAreaMarginX, INPUT_AREA__MARGIN_X_MOBILE, INPUT_AREA__MARGIN_X_DESKTOP, forceRenderViewIn);
  const responsiveInputAreaPaddingX = computeResponsiveValue(inputAreaPaddingX, INPUT_AREA__PADDING_X_MOBILE, INPUT_AREA__PADDING_X_DESKTOP, forceRenderViewIn);

  const {
    containerRef,
    fontSize: fitFontSize,
    lineCount: titleLinesCount,
    isReady: isFontSizeReady,
  } = useFitText({
    textRef: textRefToPass,
    maxFontSize: Number.parseFloat(scaleFontSize(pxToRem(computeResponsiveValue(fontSize, 64, 125, forceRenderViewIn)), Category.EVENT_TITLE)) * 16,
    // widthOffset is input area total outer margin + input area total padding
    widthOffset: responsiveInputAreaMarginX * 2 + responsiveInputAreaPaddingX * 2,
    heightOffset: INPUT_AREA__PADDING_Y * 2,
    minFontSize: 10,
  });

  const { eventInfo } = useEventInfo();

  const { tq } = useTranslation('joykit');

  useEffect(() => {
    if (editableContentRef?.current && isContentEditable) {
      editableContentRef.current.focus();
      withWindow(globalWindow => {
        // place caret at end of the text
        const range = globalWindow.document.createRange();
        range.selectNodeContents(editableContentRef.current as Node);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  }, [editableContentRef, isContentEditable]);

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  const handleInput = useEventCallback(() => {
    if (!editableContentRef?.current) return;

    const element = editableContentRef.current;
    const textContent = element.innerText;
    if (textContent.length > TEXT_MAX_CHAR_LIMIT) {
      element.innerText = textContent.slice(0, TEXT_MAX_CHAR_LIMIT);
      // Restore caret to end
      withWindow(globalWindow => {
        const range = globalWindow.document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  });

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  // if hasModifiedEventNameOnce is true then show headerTextData.content. In case of hasModifiedEventNameOnce is false or undefined we need to show the eventDisplayName
  const textContent = (headerTextData?.hasModifiedEventNameOnce ? headerTextData.content : eventDisplayName) || '';

  const { navTextColor, navEffectStyles, titleTextColor, titleEffectStyles } = useHeaderStylesForGuest({
    headerTitleStyle,
    navigationStyle,
    colorsData: colors,
  });

  const isEmpty = !textContent.trim();

  return (
    <>
      <Box
        ref={navBarRef}
        width="100%"
        pointerEvents={isContentEditable ? 'none' : 'unset'}
        position="relative"
        zIndex="docked"
        paddingBottom={isCompactView && navBarVariant === 'inline' ? 8 : 0}
        // Prevent clicks on nav items from bubbling up to the parent InlineEditor wrapper
        onClick={e => e.stopPropagation()}
      >
        <NavBar
          preventExpandingToFullWidth
          handleMenuClicked={handleMenuClicked}
          title={eventDisplayName}
          variant={navBarVariant}
          data-testid={'header-nav'}
          eventHandle={eventHandle}
          pages={pages}
          eventProps={eventProps}
          textColor={navTextColor}
          {...navEffectStyles}
          borderRadius={navigationStyle ? pxToRem(DEFAULT_BORDER_RADIUS) : undefined}
          paddingX={4}
          paddingY={2}
        />
        {/* Language picker, top-right. The full-bleed photo/video heroes render this
            standalone nav instead of EventNameNav, so the control is mounted here too —
            otherwise it disappears whenever a background hero is selected. */}
        <Flex
          position="absolute"
          top="19px"
          right="16px"
          flexDirection="column"
          alignItems="flex-end"
          gap={3}
        >
          <GuestLanguageSelector surface="header" />
        </Flex>
      </Box>
      {!isCompactView && !hideEventName && (
        <Flex
          ref={containerRef}
          position="relative"
          width="100%"
          height={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : viewportHeightFallback} - ${navBarHeight}px - ${CONTAINER_TOTAL_MARGIN}px)`}
          marginTop={8}
        >
          <Flex
            width="100%"
            maxHeight="100%"
            position="absolute"
            zIndex="docked"
            {...getVerticalPlacement(headerTextData?.pageAlignment?.y || 'center', forceRenderViewIn)}
            justifyContent={headerTextData?.pageAlignment?.x || headerTextData?.textAlignment || 'left'}
            // paddingX here is in the role of the horizontal margin of the input area
            paddingX={responsiveInputAreaMarginX}
          >
            <Box
              maxWidth="100%"
              paddingY={INPUT_AREA__PADDING_Y}
              paddingX={responsiveInputAreaPaddingX}
              {...titleEffectStyles}
              transition="opacity 150ms ease-in"
              style={{
                // Now that we're fully SSRing -- need to hide the title until the font is ready
                opacity: isFontSizeReady ? 1 : 0,
              }}
              borderRadius={headerTitleStyle ? (titleLinesCount <= FULL_BORDER_RADIUS_LINES_THRESHOLD ? FULL_BORDER_RADIUS : DEFAULT_BORDER_RADIUS) : undefined}
              // Prevent clicks on the title area from bubbling up to the parent InlineEditor wrapper
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <InlineEditor
                elementLabel={eventInfo?.eventType === EventType.wedding ? tq`Couple's Name` : tq`Event Display Name`}
                labelPosition="bottom"
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                  minWidth: isEmpty ? pxToRem(INLINE_WRAPPER_MIN_WIDTH) : 'unset',
                  minHeight: isEmpty ? pxToRem(INLINE_WRAPPER_MIN_HEIGHT) : 'unset',
                }}
                minHeight={isEmpty ? pxToRem(INLINE_WRAPPER_MIN_HEIGHT) : undefined}
                inset={isMobile ? { x: -20, y: -24 } : { x: -16, y: -20 }}
                componentName="couplesName"
                pageName={currentPageType}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  {isContentEditable ? (
                    <Box
                      position="relative"
                      minWidth={isEmpty ? pxToRem(INLINE_WRAPPER_MIN_WIDTH) : 'unset'}
                    >
                      {onTextAlignmentChange && (
                        <Box
                          position="absolute"
                          top={pxToRem(applyScaling(-80))}
                          left="50%"
                          transform="translate(-50%)"
                          zIndex="overlay"
                        >
                          <TextAlignmentToolbar
                            selectedAlignment={headerTextData?.textAlignment || 'left'}
                            onAlignmentChange={onTextAlignmentChange}
                            scaleValue={scaleValue}
                          />
                        </Box>
                      )}
                      <AnimatedBorder
                        isVisibile={true}
                        borderWidth={pxToRem(applyScaling(2))}
                        borderRadius={`${applyScaling(8)}px`}
                        inset={isMobile ? { x: '-20px', y: '-24px' } : { x: '-16px', y: '-20px' }}
                        padding={0}
                      />
                      <Box
                        ref={textRefToPass}
                        position="relative"
                        typographyVariant={isXsScreen ? 'display4' : 'display6'}
                        color={titleTextColor}
                        zIndex={11}
                        onInput={handleInput}
                        contentEditable={isContentEditable}
                        dangerouslySetInnerHTML={{ __html: textContent }}
                        textAlign={headerTextData?.textAlignment || 'left'}
                        whiteSpace="pre-wrap"
                        outline="none"
                        maxHeight={`calc(${renderedContainerHeight ? `${renderedContainerHeight}px` : viewportHeightFallback} - ${navBarHeight}px - ${CONTAINER_TOTAL_MARGIN}px - ${INPUT_AREA__PADDING_Y * 2}px)`}
                        overflow="hidden"
                        top="0"
                        style={{
                          caretColor: '#4E06F2',
                          fontSize: fitFontSize,
                        }}
                        pointerEvents="auto"
                      />
                    </Box>
                  ) : (
                    <TextV2
                      ref={textRefToPass}
                      // The LCP element, large block-level text
                      as="h1"
                      typographyVariant={isXsScreen ? 'display4' : 'display6'}
                      color={titleTextColor}
                      whiteSpace="pre-wrap"
                      textAlign={headerTextData?.textAlignment || 'left'}
                      position="relative"
                      style={{ fontSize: fitFontSize }}
                    >
                      {textContent}
                    </TextV2>
                  )}
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </Box>
          </Flex>
        </Flex>
      )}
    </>
  );
};

EventNameNavForStandaloneHeader.displayName = 'EventNameNavForStandaloneHeader';
